import { createElement, lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** A tela não carregou (pedaço da aplicação indisponível): quem trata é o RouteLoadBoundary. */
export class RouteLoadError extends Error {
  constructor(readonly cause: unknown) {
    super('Não foi possível carregar esta tela.');
    this.name = 'RouteLoadError';
  }
}

const failed = new Set<() => void>();
const RETRY_PARAM = 'tentativa';

/** "Tentar de novo": as telas que falharam voltam a pedir o próprio pedaço. */
export function retryFailedRoutes(): void {
  for (const reset of failed) reset();
  failed.clear();
}

/** O endereço do pedaço que falhou, quando o navegador o diz na mensagem do erro. Só da própria aplicação. */
export function failedChunkUrl(cause: unknown, origin: string = window.location.origin): string | null {
  const message = cause instanceof Error ? cause.message : '';
  const found = /(https?:\/\/[^\s'"]+)\s*$/.exec(message)?.[1];
  if (!found) return null;
  try {
    const url = new URL(found);
    url.searchParams.delete(RETRY_PARAM);
    return url.origin === origin ? url.toString() : null;
  } catch {
    return null;
  }
}

const importByUrl = (url: string): Promise<unknown> => import(/* @vite-ignore */ url);

/**
 * `React.lazy` guarda a falha para sempre: depois de uma falha de carregamento a tela só voltava
 * recarregando a página. Aqui a falha fica marcada e a tela pode tentar de novo sem recarregar
 * (Story 65.45: com texto digitado e não salvo, recarregar apagaria o texto).
 *
 * O navegador também guarda a falha, pelo endereço do pedaço: pedir o mesmo endereço de novo
 * falha na hora, sem novo pedido. Por isso a nova tentativa pede o mesmo arquivo por um endereço
 * com um parâmetro a mais. `pick` diz qual componente do módulo é a tela.
 */
export function lazyRoute<M = { default: ComponentType }>(
  load: () => Promise<M>,
  pick: (module: M) => ComponentType = (module) => (module as { default: ComponentType }).default,
  importUrl: (url: string) => Promise<unknown> = importByUrl,
): ComponentType {
  let Loaded: LazyExoticComponent<ComponentType>;
  let chunkUrl: string | null = null;
  let attempt = 0;
  const run = () => {
    const source = chunkUrl
      ? (importUrl(`${chunkUrl}${chunkUrl.includes('?') ? '&' : '?'}${RETRY_PARAM}=${attempt}`) as Promise<M>)
      : load();
    return source.then(
      (module) => ({ default: pick(module) }),
      (cause: unknown) => {
        chunkUrl = chunkUrl ?? failedChunkUrl(cause);
        attempt += 1;
        failed.add(reset);
        throw new RouteLoadError(cause);
      },
    );
  };
  function reset() {
    Loaded = lazy(run);
  }
  reset();
  return function LazyRoute() {
    return createElement(Loaded);
  };
}
